import { useContext } from "react";

import { useFormikContext } from "formik";
import PropTypes from "prop-types";

import { Loader } from "components/elements";
import { FormStatus } from "components/form";
import { Section, SubTab, SubTabs, TabContent } from "components/layout";
import { trans } from "src/translations";
import { isTabInvalid } from "utils/validation";

import { ManifestEditForm } from ".";
import { ManifestDataContext } from "../../ManifestData";
import { MANIFEST_ERROR_FIELD, META_FIELDS } from "../constants";
import { createTabPath, getActiveTabId, getTabsConfig } from "../utils";

function getSubmitError(error) {
  const targetsConflictMessage =
    "Cannot create manifests with overlapping targets for the same application.";
  if (
    error?.serverErrors?.some(
      (error) =>
        error?.status === "409" &&
        error?.detail?.includes(targetsConflictMessage)
    )
  ) {
    return targetsConflictMessage;
  }
  return undefined;
}

function getValidationError(errors) {
  if (META_FIELDS.appId in errors) {
    return "The application ID field is required. Please fill it and try again.";
  }
  if (MANIFEST_ERROR_FIELD in errors) {
    return "All fields related to the application configuration are empty. Please fill them and try again.";
  }
  return undefined;
}

export const ManifestEditTabs = ({
  createManifest,
  updateManifest,
  controls = false,
}) => {
  const { app, loading } = useContext(ManifestDataContext);
  const { errors } = useFormikContext();
  const tabId = getActiveTabId(app.type);

  const renderFormStatus = () => (
    <FormStatus
      submitMutations={[createManifest, updateManifest]}
      getSubmitError={getSubmitError}
      getValidationError={getValidationError}
    />
  );

  return (
    <Section header={trans.APP_DETAILS_MANIFEST__EDIT_HEADER()}>
      {loading ? (
        <Loader />
      ) : (
        <>
          {renderFormStatus()}
          <SubTabs>
            {getTabsConfig(app.type).map(({ id, fields, text }) => (
              <SubTab
                key={id}
                active={id === tabId}
                icon={
                  !isTabInvalid(
                    fields.map(({ name }) => name),
                    errors
                  )
                    ? "ok"
                    : "warning"
                }
                iconColor={
                  !isTabInvalid(
                    fields.map(({ name }) => name),
                    errors
                  )
                    ? "success"
                    : "error"
                }
                look="gray2"
                text={text()}
                to={createTabPath(id)}
              />
            ))}
          </SubTabs>
          <TabContent>
            <ManifestEditForm tabId={tabId} controls={controls} />
          </TabContent>
          {(tabId === "generic" || tabId === "navigation") &&
            renderFormStatus()}
        </>
      )}
    </Section>
  );
};

ManifestEditTabs.propTypes = {
  controls: PropTypes.bool,
};
